import { useRef } from 'react';
import { axiosClient, cdnClient, elasticSearchClient } from '..';
import {
    BusApiCalls,
    BusTicketAllocateRequest,
    BusTicketCardRequest,
    BusTicketSeatRequest,
    PassengerSaveRequest,
    getBusInformationRequest,
    PaymentRequest,
    PnrRequest,
    busCancelTicketRequest,
    SavePassengerRequest,
    ValidationErrorRequest,
    CardPaymentRequest,
    BusTravelCreateRequest,
    BusCancellationRequest,
} from './index.model';
import { mappingBusTicketCard, mappingBusTicketSeat } from './index.mapping';
import { AxiosResponse } from 'axios';

const useBusService = (): BusApiCalls => {
    const generateApiCalls = (): BusApiCalls => {
        return {
            search: async (searchText: any) => {
                const response: any = await elasticSearchClient.get('stops_v2/_search', {
                    params: {
                        source: JSON.stringify({
                            query: {
                                bool: {
                                    must: [
                                        {
                                            multi_match: {
                                                query: searchText,
                                                type: 'bool_prefix',
                                                fields: ['StopName'],
                                                // analyzer: 'turkish_analyzer',
                                            },
                                        },
                                    ],
                                    filter: [
                                        {
                                            range: {
                                                Rating: {
                                                    gt: 0,
                                                },
                                            },
                                        },
                                    ],
                                },
                            },
                            sort: [
                                {
                                    Rating: {
                                        order: 'desc',
                                    },
                                },
                            ],
                            size: 10,
                        }),
                        source_content_type: 'application/json',
                    },
                });
                return response;
            },
            searchOneCode: async (searchText: any) => {
                const response: any = await elasticSearchClient.get('stops_v2/_search', {
                    params: {
                        source: JSON.stringify({
                            query: {
                                bool: {
                                    must: [
                                        {
                                            multi_match: {
                                                query: searchText,
                                                type: 'bool_prefix',
                                                fields: ['ID'],
                                            },
                                        },
                                    ],
                                },
                            },
                            sort: [
                                {
                                    Rating: {
                                        order: 'desc',
                                    },
                                },
                            ],
                            size: 10,
                        }),
                        source_content_type: 'application/json',
                    },
                });
                return response;
            },
            searchPopularCities: async (list: any) => {
                const response: any = await elasticSearchClient.get('stops_v2/_search', {
                    params: {
                        source: JSON.stringify({
                            query: {
                                bool: {
                                    must: [{ terms: { ID: list } }],
                                },
                            },
                        }),
                        source_content_type: 'application/json',
                    },
                });
                return response;
            },
            busTicketSearch: async (props: BusTicketCardRequest) => {
                const response: AxiosResponse = await axiosClient.post(`/bus/search`, props);
                return mappingBusTicketCard(response.data);
            },
            busTicketSeat: async (props: BusTicketSeatRequest) => {
                const response: AxiosResponse = await axiosClient.post(`/bus/seats`, props);
                return mappingBusTicketSeat(response.data);
            },
            busTicketAllocate: async (props: BusTicketAllocateRequest) => {
                const response: AxiosResponse = await axiosClient.post(`/bus/allocate`, props);
                return response.data;
            },
            travelGetPrice: async (props: BusTravelCreateRequest) => {
                const response: AxiosResponse = await axiosClient.post(`/bus/insurance/travelgetprice`, props);
                return response;
            },
            travelCreateOffer: async (props: BusTravelCreateRequest) => {
                const response: AxiosResponse = await axiosClient.post(`/bus/insurance/travelcreateoffer`, props);
                return response;
            },
            cancellationGetPrice: async (props: BusCancellationRequest) => {
                const response: AxiosResponse = await axiosClient.post(`/bus/insurance/cancel_protection/price`, props);
                return response;
            },
            cancellationCreateOffer: async (props: BusCancellationRequest) => {
                const response: AxiosResponse = await axiosClient.post(`/bus/insurance/cancel_protection/offer`, props);
                return response;
            },

            getBusInformation: async (props: getBusInformationRequest) => {
                const response: AxiosResponse = await axiosClient.post(`/bus/businfo`, props);
                return response.data;
            },
            busCancelTicket: async (props: busCancelTicketRequest) => {
                const response: AxiosResponse = await axiosClient.post(`/bus/cancelTicket`, props);
                return response.data;
            },
            savePassenger: async (props: PassengerSaveRequest) => {
                const response: AxiosResponse = await axiosClient.post(`/bus/passenger`, props);
                return response.data;
            },
            cardPayment: async (props: PaymentRequest) => {
                const response: AxiosResponse = await axiosClient.post(`/bus/cardpayment`, props);
                return response.data;
            },
            cardPaymentParam: async (props: CardPaymentRequest) => {
                const response: AxiosResponse = await axiosClient.post(`/bus/cardPaymentParam`, props);
                return response.data;
            },
            pnrControl: async (props: PnrRequest) => {
                const response: AxiosResponse = await axiosClient.post(`/bus/pnrcontrol`, props);
                return response.data;
            },
            getSavePassengers: async (props: SavePassengerRequest) => {
                const response: AxiosResponse = await axiosClient.post(`/bus/getsavepassengers`, props);
                return response.data;
            },
            validationErrorSave: async (props: ValidationErrorRequest) => {
                const response: AxiosResponse = await axiosClient.post(`/bus/save_validate_error`, props);
                return response.data;
            },
            getBusCompanies: async () => {
                const response: AxiosResponse = await axiosClient.get(`/bus/bus_companies`);
                return response.data;
            },
            getStationsCDN: async () => {
                return cdnClient.get('/uploads/stations.json');
            },
            applyPromotionCode: async (body: any) => {
                const response: AxiosResponse = await axiosClient.post('/bus/applyPromotionCode', body);
                return response.data;
            },
        };
    };
    const callsRef = useRef<BusApiCalls>(generateApiCalls());

    return callsRef.current;
};

export default useBusService;
